import React from "react"
import { cloneDeep } from "lodash"
import { useShallow } from "zustand/react/shallow"
import { useParams } from "react-router-dom"
import { Tooltip, Flex } from "antd"
import Checkbox from "components/Checkbox/Checkbox"
import { TextInput, PrimeButton, StatusBox } from "components-design-system"
import Modal from "components/Modal/Modal"
import BulkEditPanel from "../BulkEditPanel/BulkEditPanel"
import StudyLocationsGrid from "./StudyLocationsGrid"
import NewSiteDetails from "components/NewSiteDetails/NewSiteDetails"
import EditSiteDetails from "components/EditSiteDetails/EditSiteDetails"
import DeleteSite from "components/DeleteSite/DeleteSite"
import PRSValidationMessages from "components/PRSValidationMessages/PRSValidationMessages"
import { Panel } from "components-design-system"
import "./StudyLocationDetails.scss"

// hooks
import { useUserPermissions } from "api/hooks"
import {
  useGetSiteManagementStudy,
  usePutSiteManagementLocation,
  useDeleteSiteManagementLocation,
  useGetSiteManagementMasking,
  useGetSiteManagementLocations,
} from "api/hooks/siteManagement/useSiteManagementApi"
import { useGetStudy } from "api/hooks/studies/useStudiesApi"
import useAuthoringValidationsStore from "../../Authoring/hooks/stores/useAuthoringValidationsStore"

import useStudyDetailsHeader from "./useStudyDetailsHeader"

const StudyLocationDetails = ({ setTriggerExport, triggerExport, locationStats, actionProps }) => {
  const { studyId, siteManagementId } = useParams()
  const { data: permissions } = useUserPermissions()
  const { data: study, refetch: refetchStudy } = useGetStudy(studyId)
  const { sitesValidations } = useAuthoringValidationsStore(
    useShallow((state) => ({
      sitesValidations: state.sitesValidations,
    }))
  )
  const [siteManagementMaskingSetting, setSiteManagementMaskingSetting] = React.useState({})

  const { data: siteManagementLocations, refetch: refetchSiteManagementLocations } =
    useGetSiteManagementLocations(siteManagementId)

  const {
    UploadButton = null,
    RegenerateIconButton = null,
    SiteMenu = null,
    ValidateButton = null,
    isUploading = null,
    CancelButton = null,
    badges = null,
    displayNonSiteErrors = null,
    PRSValidationModal = null,
    UploadModals = null,
  } = useStudyDetailsHeader({
    siteManagementMaskingSetting,
    setSiteManagementMaskingSetting,
    ...actionProps,
  })
  const { data: siteManagementStudy } = useGetSiteManagementStudy(siteManagementId)

  const { data: siteManagementMasking } = useGetSiteManagementMasking(siteManagementId)

  // apis
  const putSiteManagementLocation = usePutSiteManagementLocation(siteManagementId)
  const deleteSiteManagementLocation = useDeleteSiteManagementLocation(siteManagementId)

  const [search, setSearch] = React.useState("")
  const [openModal, setOpenModal] = React.useState(false)
  const [modalConfig, setModalConfig] = React.useState({})
  const [disabledAdd, setDisabledAdd] = React.useState(false)

  const [siteIDSet, setSiteIDSet] = React.useState(new Set())
  const [selectedFilter, setSelectedFilter] = React.useState(null)
  const [rowData, setRowData] = React.useState(siteManagementLocations)
  const [isCtgSorted, setIsCtgSorted] = React.useState(false)

  // edit/delete rows
  const [selectedRows, setSelectedRows] = React.useState([])
  const [gridApi, setGridApi] = React.useState()

  React.useEffect(() => {
    if (siteManagementStudy?.only_on_ctg) {
      setDisabledAdd(true)
    }
  }, [siteManagementStudy])

  React.useEffect(() => {
    if (siteManagementMasking) {
      let { masking_config } = siteManagementMasking
      if (masking_config) {
        setSiteManagementMaskingSetting({
          ...masking_config,
        })
      }
    }
  }, [siteManagementMasking])

  React.useEffect(() => {
    if (siteManagementLocations && Array.isArray(siteManagementLocations)) {
      let siteIDSet = new Set()
      siteManagementLocations.forEach((site) => {
        siteIDSet.add(site.site_id)
      })
      setSiteIDSet(siteIDSet)
    }
  }, [siteManagementLocations])

  React.useEffect(() => {
    if (selectedFilter) {
      switch (selectedFilter) {
        case "total_prime":
          formatRowData(siteManagementLocations)
          break
        case "total_ctg":
          formatRowData(locationStats.total_ctg.studies)
          break
        case "prime_ctg_differences":
          formatRowData(locationStats.prime_ctg_differences.studies)
          break
        case "ctg_submission_differences":
          formatRowData(locationStats.ctg_submission_differences.studies)
          break

        default:
          formatRowData(siteManagementLocations)
      }
    } else {
      formatRowData(siteManagementLocations)
    }
  }, [selectedFilter, siteManagementLocations, sitesValidations])

  const formatRowData = (sites) => {
    if (sitesValidations?.length > 0) {
      let valMap = {}

      sitesValidations.forEach((validation) => {
        if (valMap[validation.site_db_id]) {
          valMap[validation.site_db_id].push(validation)
        } else {
          valMap[validation.site_db_id] = [validation]
        }
      })

      let newRowData = sites.map((site) => {
        return {
          ...site,
          validations: valMap[site.id] ? valMap[site.id] : [],
        }
      })
      setRowData(newRowData)
    } else {
      setRowData(sites)
    }
  }

  const onOpenModal = (variant, gridProps, data, messages) => {
    switch (variant) {
      case "add-site":
        setModalConfig({
          label: study.study_id,
          header: "New Site",
          subheader: study.study_id,
          children: (
            <NewSiteDetails
              openModal={openModal}
              siteIDSet={siteIDSet}
              setOpenModal={setOpenModal}
              siteManagementMaskingSetting={siteManagementMaskingSetting}
            />
          ),
        })
        setOpenModal(true)
        break
      case "edit-site":
        setModalConfig({
          header: "Edit Site Details",
          subheader: study.study_id,
          children: (
            <EditSiteDetails
              messages={messages}
              onOpenModal={onOpenModal}
              setOpenModal={setOpenModal}
              onEditSite={onEditSite}
              gridProps={gridProps}
            />
          ),
        })
        setOpenModal(true)
        break
      // TODO: remove if not being used
      case "delete-site":
        setModalConfig({
          label: study.study_id,
          header: "Delete Site",
          subheader: "Are you sure you would like to delete this site? This cannot be undone.",
          size: "sm",
          children: (
            <DeleteSite
              onOpenModal={onOpenModal}
              setOpenModal={setOpenModal}
              onDeleteSite={onDeleteSite}
              gridProps={gridProps}
              formData={data}
            />
          ),
        })
        setOpenModal(true)
        break
      case "prs-validation-messages":
        let count = 0
        messages.forEach((item) => {
          if (item.severity === "ERROR") count++
        })
        setModalConfig({
          header: "PRS Validation Messages",
          subheader:
            "There are are [" +
            count +
            "] errors that must be updated in order to release record to CTG. Errors must be addressed. Warnings and notes should be addressed as needed. To refresh PRS validation messages, studies must be resubmitted.",
          children: (
            <PRSValidationMessages
              gridProps={gridProps}
              studyData={data}
              messages={messages}
              onOpenModal={onOpenModal}
              setOpenModal={setOpenModal}
              origin={"locations"}
              hideSubmit={true}
            />
          ),
        })
        setOpenModal(true)
        break
      default:
        return
    }
  }

  const onEditSite = (gridProps, newData) => {
    let updatedData = {
      ...gridProps.data,
      location_data: newData,
      site_id: newData.site_id,
    }

    let body = cloneDeep(updatedData)

    delete body.location_data.site_id
    delete body.location_data.validation_messages

    putSiteManagementLocation(
      {
        id: gridProps.data.id + "/",
        body,
      },
      {
        onSuccess: () => {
          setOpenModal(false)
          refetchStudy()
        },
      }
    )
  }

  const onDeleteSite = (gridProps) => {
    deleteSiteManagementLocation(
      { id: gridProps.data.id + "/" },
      {
        onSuccess: () => {
          setOpenModal(false)
          refetchStudy()
        },
      }
    )
  }

  const renderTooltip = (tooltipProps) => {
    let text = "No edits can be made as this study does not exist in Source and will not be able to be uploaded."

    return (
      <div
        id="disabled-add-new-site-tooltip"
        {...tooltipProps}
      >
        <div className="new-site-tooltip">{text}</div>
      </div>
    )
  }

  let filterSchema = {
    total_prime: {
      name: "Total Sites in Prime",
      value: locationStats.total_prime.count,
      style: { marginRight: 16 },
    },
    total_ctg: {
      name: "Total Sites in CTG",
      value: locationStats.total_ctg.count,
      style: { marginRight: 16 },
    },
    prime_ctg_differences: {
      name: "Source (Prime) and CTG Differences",
      value: locationStats.prime_ctg_differences.count,
      style: { marginRight: 16 },
    },
    ctg_submission_differences: {
      name: "CTG and Submission Differences",
      value: locationStats.ctg_submission_differences.count,
    },
  }

  const handleFilter = (key) => {
    setSelectedFilter(key)
  }

  const onSelectionChanged = ({ api }) => {
    if (!gridApi && api) {
      setGridApi(api) // set gridAPi the first time something is selected
    }
    setSelectedRows(api.getSelectedRows())
  }

  const renderFilters = () => {
    return Object.keys(filterSchema).map((key) => {
      return (
        <StatusBox
          key={key}
          {...filterSchema[key]} // name, value, style
          onClick={() => handleFilter(key)}
          active={key === selectedFilter}
          style={{ height: "85px", width: "160px" }}
        />
      )
    })
  }

  const ctgSortStudies = JSON.parse(localStorage.getItem("ctgSortStudies")) || {}

  const onSortCtgLocations = (isSorted) => {
    let tempSortObj = { ...ctgSortStudies }

    if (isSorted) {
      tempSortObj[siteManagementId] = true
      setIsCtgSorted(true)
    } else {
      delete tempSortObj[siteManagementId]
      setIsCtgSorted(false)
    }

    localStorage.setItem("ctgSortStudies", JSON.stringify(tempSortObj))
    setTimeout(() => {
      refetchSiteManagementLocations()
    }, [1000])
  }

  React.useEffect(() => {
    setIsCtgSorted(ctgSortStudies[siteManagementId] ? true : false)
  }, [siteManagementId])

  const actions = React.useMemo(() => {
    const ret = []
    if (permissions?.["study.site.create"]) {
      if (disabledAdd) {
        ret.push(
          <Tooltip title={(props) => renderTooltip(props)}>
            <div
              style={{
                display: "inline-block",
                float: "right",
                cursor: "not-allowed",
              }}
            >
              <PrimeButton
                disabled={disabledAdd}
                onClick={() => onOpenModal("add-site")}
                style={{ float: "right", pointerEvents: "none" }}
                tooltip="Add New Site"
              />
            </div>
          </Tooltip>
        )
      } else {
        ret.push(
          <PrimeButton
            icon={<i className="fa-light fa-plus"></i>}
            tooltip="Add New Site"
            onClick={() => onOpenModal("add-site")}
          />
        )
      }
    }

    ret.push(RegenerateIconButton)

    ret.push([ValidateButton])

    if (permissions?.["study.site.upload"]) {
      ret.push(UploadButton)
    }

    ret.push(SiteMenu)

    return ret
  }, [permissions, siteManagementMaskingSetting, isUploading, actionProps?.isUploadDisabled])

  return (
    <Panel
      title={
        <Flex
          align="center"
          gap={12}
        >
          <span>SITES</span>
          <Flex
            align="center"
            gap={6}
          >
            {badges}
          </Flex>
        </Flex>
      }
      iconClassName="fa-light fa-location-dot"
      className={`site-details-content`}
      actions={actions}
    >
      <Flex
        vertical={true}
        gap={12}
      >
        <Flex
          className="site-details-header"
          justify="space-between"
        >
          <Flex
            style={{ width: "100%" }}
            vertical={true}
            justify="space-between"
          >
            <Flex
              gap={16}
              justify="space-between"
              style={{ width: "100%" }}
            >
              <div md={8}>
                <div className="status-box-wrap">{renderFilters()}</div>
              </div>
              {displayNonSiteErrors()}
            </Flex>
            <div className="locations-sub-actions">
              <TextInput
                placeholder={"Search using any data value"}
                className="study-details-content__search"
                onChange={(evt) => setSearch(evt.target.value)}
              />
              <Checkbox
                checked={isCtgSorted}
                onChange={(e) => onSortCtgLocations(e.target.checked)}
                label="Sort by CTG order"
              />
            </div>
          </Flex>
        </Flex>
        <BulkEditPanel
          study={study}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          gridApi={gridApi}
          refetchStudy={refetchStudy}
        />
        <StudyLocationsGrid
          search={search}
          rowData={rowData}
          study={study}
          onOpenModal={onOpenModal}
          setTriggerExport={setTriggerExport}
          triggerExport={triggerExport}
          permissions={permissions}
          onSelectionChanged={onSelectionChanged}
        />
        <Modal
          title={modalConfig.header}
          message={modalConfig.subheader}
          onCancel={() => setOpenModal(false)}
          hideImage={true}
          footer={[]}
          width={720}
          size={modalConfig.size}
          open={openModal}
          destroyOnClose={true}
        >
          {modalConfig.children}
        </Modal>
        {PRSValidationModal}
      </Flex>
      {UploadModals}
    </Panel>
  )
}

export default StudyLocationDetails
